/** Client-safe guard narrowing a response body onto the `ApiResponse` union: the wire `success` tag, not the caller's declared type, decides the branch. */

import { safeParseResponseJson } from '@federa/utils/http/parse-json-body';
import { isPlainObject } from '@federa/const/record-guards';
import { isSuccess } from '@federa/const/http-status';
import { stringOrEmpty } from '@federa/const/string-guards';
import { ERROR } from '@federa/const/error-values';
import type { ApiResponse } from '@federa/types/api-response';

/** Success verdict READ off the WIRE body, falling back to the HTTP status -- the single home of the `bodyTag ?? isSuccess(status)` rule. The LENIENT reader. */
export function resolveEnvelopeSuccess(body: unknown, httpStatus: number): boolean {
	if (isPlainObject(body) && typeof body['success'] === 'boolean') return body['success'];
	return isSuccess(httpStatus);
}

/** Narrow an already-parsed JSON body onto the ApiResponse union: a non-object body, or a `success` that is not literally `true`, maps to the failure variant (fail closed); the success variant carries the full body record as `data`. Not exported: `parseApiEnvelope` below is its single consumer. */
function toApiEnvelope(body: unknown, status: number): ApiResponse<Record<string, unknown>> {
	if (!isPlainObject(body)) {
		return { success: false, status, error: 'Response body is not a JSON object', raw: body };
	}
	const record = body;
	if (record['success'] !== true) {
		const errValue = record['error'];
		return {
			success: false,
			status,
			error: stringOrEmpty(errValue),
			raw: body,
		};
	}
	return { success: true, status, data: record, raw: body };
}

/**
 * The LEGACY failure message: the `message` key off the RAW body, for the producers that answer with one instead of the
 * canonical `error`. `undefined` when the raw body is absent or carries no `message`, so a caller's
 * `envelope.error || legacyEnvelopeMessage(envelope) || <default>` chain falls straight through.
 *
 * A COMPATIBILITY read, not a second envelope contract: `toApiEnvelope` above already states that a producer answering a
 * different key is reconciled AT THE PRODUCER. Until each one is, the raw-body cast lives here once rather than being
 * respelled per call site, where the two copies had already drifted into different fallback chains.
 */
export function legacyEnvelopeMessage(envelope: ApiResponse<Record<string, unknown>>): string | undefined {
	return ((envelope.raw ?? {}) as { message?: string }).message;
}

/**
 * The most specific thing this envelope can say about itself, for an error MESSAGE rather than for control flow.
 * A success envelope has only its raw body to offer; a failure envelope prefers its canonical `error` text and falls
 * back to the raw body when the producer sent none. Callers embed the result as `String(detail ?? '')`.
 *
 * Read-only diagnostics: nothing branches on this value, so the union arms are collapsed on purpose.
 */
export function envelopeDetail(envelope: ApiResponse<Record<string, unknown>>): unknown {
	return envelope.success ? envelope.raw : envelope.error || envelope.raw;
}

/** Parse a fetch Response into the ApiResponse union without throwing on envelope shape -- JSON parse failures map to the failure variant. The failure message is read from the body's `error` key, the one key every failure envelope this repo emits uses; a producer that answers a different key is reconciled at the producer, not by a per-call override here. */
export async function parseApiEnvelope(response: Response): Promise<ApiResponse<Record<string, unknown>>> {
	const parseResult = await safeParseResponseJson(response);
	if (!parseResult.ok) {
		return { success: false, status: response.status, error: ERROR.FAILED_TO_PARSE_RESPONSE_JSON };
	}
	return toApiEnvelope(parseResult.data, response.status);
}
